<template>
  <BittsModal
    :loading="isResuming"
    :width="440"
    :title="title"
    :description="description"
    aria-label="Install the Crossbeam app in Salesforce"
    :hide-footer="true"
    :use-mask-to-close="false"
    @closed="cancel"
  >
    <template #image>
      <div class="c-salesforce-app-install__logo">
        <BittsSvg svg="salesforceIcon" class="w-64" />
      </div>
    </template>
    <BittsButton
      size="large"
      :text="installButtonText"
      :loading="isWaitingForInstall"
      class="w-full flex justify-center"
      data-testid="salesforce-install-button"
      @click="openInstall"
    />
    <BittsButton
      size="large"
      type="neutral"
      variant="outline"
      text="Continue setup"
      class="w-full flex justify-center mt-12"
      data-testid="salesforce-install-resume-button"
      @click="resumeConnection"
    />
    <BittsCallout
      size="small"
      type="info"
      class="mt-24"
      title="Not a Salesforce admin?"
      subtitle="Send the link to someone who is. Once they confirm it is installed, continue setup here."
      :action-text="copyButtonText"
      :action-icon="hasCopied ? ['fas', 'check'] : ['fak', 'copy']"
      action-button-type="neutral"
      data-testid="salesforce-install-callout"
      @bitts-callout-action="copyInstallLink"
    />
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsCallout,
  BittsModal,
  BittsSvg,
} from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { crossbeamApi } from '@/api';
import { useCopyToClipboard } from '@/composables/useCopyToClipboard';
import usePolling from '@/composables/usePolling';
import { captureException } from '@/errors';
import { useFeedsStore, useFlashesStore } from '@/stores';
import { Feed } from '@/types/feeds';
import {
  clearSalesforceAppInstall,
  readSalesforceAppInstall,
} from '@/utils/salesforceAppInstall';

useHead({ title: 'Install the Crossbeam app - Crossbeam' });

const router = useRouter();
const route = useRoute();
const { addErrorFlash } = useFlashesStore();
const feedsStore = useFeedsStore();
const { hasCopied, copy } = useCopyToClipboard();

const prefix = route.path.startsWith('/onboarding')
  ? '/onboarding'
  : '/data-sources';

const install = readSalesforceAppInstall();

const isWaitingForInstall = ref(false);
const isResuming = ref(false);

const FEED_POLL_MS = 10000;
const FEED_POLL_LIMIT = (30 * 60 * 1000) / FEED_POLL_MS;
const INSTALL_WINDOW_POLL_MS = 1000;

let installWindow: Window | null = null;
let preexistingFeedIds: Set<number> | null = null;
let connectedFeedId: number | undefined;
let isCancelled = false;

const hasRetried = computed(() => (install?.attempts ?? 0) > 1);

const title = computed(() =>
  hasRetried.value
    ? 'Still waiting on Salesforce'
    : 'Install Crossbeam in Salesforce',
);

const description = computed(() =>
  hasRetried.value
    ? 'Salesforce cannot see the app yet. A large install takes a few minutes to finish, so it is worth trying again shortly.'
    : 'Your org needs the Crossbeam app before it can connect. This opens in a separate window, and we will pick the connection back up as soon as you close it.',
);

const installButtonText = computed(() =>
  isWaitingForInstall.value
    ? 'Waiting for Salesforce'
    : 'Install in Salesforce',
);

const copyButtonText = computed(() =>
  hasCopied.value ? 'Copied' : 'Copy link',
);

function salesforceFeedIds(): number[] {
  return feedsStore
    .getFeedsByDataSourceType('salesforce')
    .map((feed: Feed) => feed.id);
}

/* Covers an admin who installs and connects in a session of their own */
async function pollForConnectedFeed() {
  await feedsStore.refreshFeedsStore();
  if (isResuming.value) return;
  const feedIds = salesforceFeedIds();
  const baseline = preexistingFeedIds;
  if (!baseline) {
    preexistingFeedIds = new Set(feedIds);
    return;
  }
  connectedFeedId = feedIds.find((id) => !baseline.has(id));
}

async function finishOnConnectedFeed() {
  if (!connectedFeedId) return;
  installWindowPoll.cancel();
  clearSalesforceAppInstall();
  await router.push({ path: `${prefix}/feed/${connectedFeedId}` });
}

/* No noopener: it would null the handle installWindowPoll reads */
function openInstall() {
  if (!install) return;
  installWindow = window.open(
    install.installUrl,
    'salesforceAppInstall',
    'popup,width=1100,height=900',
  );
  if (!installWindow) {
    addErrorFlash({
      message: 'Could not open Salesforce',
      description:
        'Allow pop-ups for this site, or copy the install link and open it yourself.',
    });
    return;
  }
  isWaitingForInstall.value = true;
  installWindowPoll.cancel();
  installWindowPoll.start();
}

async function copyInstallLink() {
  if (!install) return;
  await copy(install.installUrl);
}

async function resumeConnection() {
  if (!install || isResuming.value) return;
  installWindowPoll.cancel();
  feedPoll.cancel();
  isResuming.value = true;
  const params = {
    integration_type: install.integrationType,
    settings_usecase: install.settingsUsecase,
    ...(install.isSandbox ? { form_properties: { is_sandbox: true } } : {}),
  };
  try {
    const { data, error } = await crossbeamApi.POST('/v0.1/oauth/authorize', {
      body: { params },
    });
    if (error) throw error;
    /* The modal closed mid-request; navigating now would yank the tab away */
    if (isCancelled) return;
    window.location.href = data.authorization_url;
  } catch (err) {
    captureException(err);
    isResuming.value = false;
    addErrorFlash({
      message: 'Could not reconnect to Salesforce',
      description: 'Please try connecting the data source again.',
    });
    await cancel();
  }
}

async function cancel() {
  isCancelled = true;
  installWindowPoll.cancel();
  feedPoll.cancel();
  clearSalesforceAppInstall();
  await router.push({ path: prefix });
}

const feedPoll = usePolling({
  interval: FEED_POLL_MS,
  immediate: true,
  poller: pollForConnectedFeed,
  shouldComplete(_response, calls = 0) {
    return Boolean(connectedFeedId) || calls >= FEED_POLL_LIMIT;
  },
  onComplete: finishOnConnectedFeed,
});

const installWindowPoll = usePolling({
  interval: INSTALL_WINDOW_POLL_MS,
  poller() {
    return Promise.resolve(installWindow?.closed);
  },
  shouldComplete(isClosed) {
    return Boolean(isClosed);
  },
  onComplete: resumeConnection,
});

if (install) feedPoll.start();
else cancel();
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-salesforce-app-install__logo {
  @apply rounded-full w-120 h-120 mb-8 flex items-center justify-center
    bg-top-gradient from-info-accent/20 to-info-accent/0;
}
</style>
